import { viewportWidth as layoutViewportWidth } from "../utils/displayDensity";
/* eslint-disable */
import { GalleryHorizontalEnd, X, RotateCcw } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import VirtualizedDropdown from "../components/VirtualizedDropdown/VirtualizedDropdown";
import MindshareHeaderControls, { MarketMindIcon } from "../components/MindshareHeaderControls";
import { MGIconButton, MGSectionHeader } from "../components/editorial/MGEditorial";
import XboxActivityMix from "../components/xbox/XboxActivityMix";
import XboxActivityTable from "../components/xbox/XboxActivityTable";
import XboxHistoryChart, { XBOX_CHART_MAX } from "../components/xbox/XboxHistoryChart";
import XboxMarketMind from "../components/xbox/XboxMarketMind";
import XboxTitleCardGrid from "../components/xbox/XboxTitleCardGrid";
import { DashboardContent } from "../components/DashboardLoader";
import { ESTIMATED_ACTIVE_DISCLOSURE, fmtInt, fmtSnapshot } from "../components/xbox/xboxUi";
import {
  XBOX_CONSOLES,
  XBOX_MODES,
  getFilteredActivityMix,
  getModeRows,
  useXboxDataset,
} from "../services/xboxDataService";
import "./xbox.css";

const VIEWS = [
  { value: "cards", label: "Cards" },
  { value: "market", label: "Market Mind" },
];
const HEADER_VIEWS = [
  { mode: "cards", label: "Cards", Icon: GalleryHorizontalEnd },
  { mode: "market", label: "Market Mind", Icon: MarketMindIcon },
];

const DEFAULT_MODE = XBOX_MODES.ALL;

const MODE_COPY = {
  [XBOX_MODES.GAMES]: { noun: "Games", unit: "game players", scope: "Game Activity", base: "all observed game activity" },
  [XBOX_MODES.APPS]: { noun: "Apps", unit: "app users", scope: "App Activity", base: "all observed app activity" },
  [XBOX_MODES.ALL]: { noun: "Titles", unit: "active users", scope: "All Activity", base: "all observed activity" },
};

function useViewportWidth() {
  const [w, setW] = useState(() => (typeof window === "undefined" ? 1600 : layoutViewportWidth()));
  useEffect(() => {
    const onResize = () => setW(layoutViewportWidth());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return w;
}

// Same column steps as the Twitch card grid.
function cardColumnsFor(width) {
  const avail = width - 48;
  if (avail >= 2100) return 10;
  if (avail >= 1700) return 9;
  if (avail >= 1420) return 8;
  if (avail >= 1180) return 7;
  if (avail >= 960) return 6;
  if (avail >= 760) return 5;
  if (avail >= 520) return 3;
  return 2;
}

const toOptions = (values) => values.map((v) => ({ value: v, label: v }));

export default function Xbox() {
  const location = useLocation();
  const { currentUser } = useAuth();
  // Signed-in users without the xbox permission get a locked public preview:
  // the overview stays visible while the activity table is veiled from the
  // first row down and all options are disabled. "?preview=public" forces the
  // preview for any user (testing/demos); it only ever restricts, never grants.
  const forcePublicPreview = new URLSearchParams(location.search).get("preview") === "public";
  const isPublicPreview = forcePublicPreview || (Boolean(currentUser) && !currentUser.is_admin &&
    !(Array.isArray(currentUser.permissions) && currentUser.permissions.includes("xbox")));
  const { dataset, loading, error, retry } = useXboxDataset();
  const viewportWidth = useViewportWidth();

  const [mode, setMode] = useState(DEFAULT_MODE);
  const [view, setView] = useState("cards");
  const [searchTerm, setSearchTerm] = useState("");
  const [showOverview, setShowOverview] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [genres, setGenres] = useState([]);
  const [publishers, setPublishers] = useState([]);
  const [consoles, setConsoles] = useState([]);
  const [gamePass, setGamePass] = useState(null);
  const [chartIds, setChartIds] = useState([]);

  const modeRows = useMemo(() => (dataset ? getModeRows(dataset, mode) : []), [dataset, mode]);
  const allActivityRows = useMemo(
    () => (dataset ? getModeRows(dataset, XBOX_MODES.ALL) : []),
    [dataset]
  );
  useEffect(() => {
    if (!modeRows.length) return;
    setChartIds((ids) => {
      const valid = ids.filter((id) => modeRows.some((r) => r.titleId === id));
      return valid;
    });
  }, [modeRows]);

  const genreOptions = useMemo(
    () => toOptions(Array.from(new Set(modeRows.flatMap((r) => r.title.genres))).sort()),
    [modeRows]
  );
  const publisherOptions = useMemo(
    () => toOptions(Array.from(new Set(modeRows.map((r) => r.title.publisher).filter(Boolean))).sort((a, b) => a.localeCompare(b))),
    [modeRows]
  );

  const matchesCommonFilters = (r) => {
    const q = searchTerm.trim().toLowerCase();
    const t = r.title;
    if (q && !t.name.toLowerCase().includes(q) && !t.publisher.toLowerCase().includes(q)) return false;
    if (genres.length && !t.genres.some((x) => genres.includes(x))) return false;
    if (publishers.length && !publishers.includes(t.publisher)) return false;
    const eligibleDevices = r.isInactive ? r.historicalObservedDevices : r.observedDevices;
    if (consoles.length && !Object.keys(eligibleDevices || {}).some((d) => consoles.includes(d))) return false;
    return true;
  };

  const pieRows = useMemo(
    () => allActivityRows.filter(matchesCommonFilters),
    [allActivityRows, searchTerm, genres, publishers, consoles]
  );
  const pieTitleIds = useMemo(() => new Set(pieRows.map((r) => r.titleId)), [pieRows]);

  const filteredRows = useMemo(() => modeRows.filter((r) => {
    if (!pieTitleIds.has(r.titleId)) return false;
    if (gamePass === "yes" && r.title.gamePass !== true) return false;
    if (gamePass === "no" && r.title.gamePass === true) return false;
    return true;
  }), [modeRows, pieTitleIds, gamePass]);

  const pieMix = useMemo(() => getFilteredActivityMix(pieRows), [pieRows]);

  const filtersActive =
    !!searchTerm.trim() || genres.length || publishers.length || consoles.length || gamePass;

  const filteredEstimate = useMemo(
    () => (filtersActive || mode !== XBOX_MODES.ALL
      ? filteredRows.reduce((sum, r) => sum + (r.estimatedPlayers || 0), 0)
      : null),
    [filtersActive, mode, filteredRows]
  );

  const columns = 5;
  const cardRows = useMemo(() => filteredRows.filter((row) => row.activeUsers > 0).slice(0, 10), [filteredRows]);

  const toggleChart = useCallback((id) => {
    if (isPublicPreview) return;
    setChartIds((ids) => {
      if (ids.includes(id)) return ids.filter((x) => x !== id);
      if (ids.length >= XBOX_CHART_MAX) return ids;
      return [...ids, id];
    });
  }, [isPublicPreview]);

  useEffect(() => {
    if (isPublicPreview) setFiltersOpen(false);
  }, [isPublicPreview]);

  const resetFilters = () => {
    setSearchTerm("");
    setGenres([]);
    setPublishers([]);
    setConsoles([]);
    setGamePass(null);
  };

  const resetOverviewFilters = () => {
    setMode(DEFAULT_MODE);
    setGamePass(null);
  };

  const selectOverviewMode = (nextMode) => {
    setMode(nextMode);
    setGamePass(null);
  };

  const copy = MODE_COPY[mode] || MODE_COPY[XBOX_MODES.GAMES];

  return (
    <div className={`xbox-page${view === "market" ? " is-market" : ""}`}>
      <MindshareHeaderControls visible neutralViews views={HEADER_VIEWS}
        targetId="xbox-header-controls" savedControlsId="xbox-header-saved-controls"
        centerSearchTargetId="xbox-header-search" ariaLabel="Xbox views and filters"
        searchAriaLabel="Search Xbox games" viewMode={view} onViewChange={isPublicPreview ? () => {} : setView}
        readOnly={isPublicPreview}
        onOpenFilters={isPublicPreview ? () => {} : () => setFiltersOpen(open => !open)} filtersOpen={!isPublicPreview && filtersOpen}
        filtersActive={!isPublicPreview && Boolean(filtersActive)} searchTerm={isPublicPreview ? "" : searchTerm} onSearchChange={isPublicPreview ? () => {} : setSearchTerm} />
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Jura:wght@300;400;500;600;700&display=swap" />
      {filtersOpen && !isPublicPreview && <div className="mg-steam-filter-backdrop" onClick={() => setFiltersOpen(false)} aria-hidden="true" />}

      <div className={`xbox-filter-popover${filtersOpen && !isPublicPreview ? " is-open" : ""}`} onMouseLeave={() => setFiltersOpen(false)}>
        <div className="xbox-filter-views" role="tablist" aria-label="Xbox view">
          {VIEWS.map((v) => (
            <button
              key={v.value}
              type="button"
              role="tab"
              aria-selected={view === v.value}
              className={`xbox-view-tab${view === v.value ? " is-active" : ""}`}
              onClick={() => setView(v.value)}
            >
              {v.label}
            </button>
          ))}
        </div>
        <div className="xbox-filter-grid">
          <div className="xbox-filter-field">
            <label>Genre</label>
            <VirtualizedDropdown
              placeholder="All Genres"
              options={genreOptions}
              isMulti
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              value={genres.map((v) => ({ value: v, label: v }))}
              onChange={(opts) => setGenres((opts || []).map((o) => o.value))}
            />
          </div>
          <div className="xbox-filter-field">
            <label>Publisher</label>
            <VirtualizedDropdown
              placeholder="All Publishers"
              options={publisherOptions}
              isMulti
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              value={publishers.map((v) => ({ value: v, label: v }))}
              onChange={(opts) => setPublishers((opts || []).map((o) => o.value))}
            />
          </div>
          <div className="xbox-filter-field">
            <label>Observed on console</label>
            <VirtualizedDropdown
              placeholder="All Xbox consoles"
              options={toOptions(XBOX_CONSOLES)}
              isMulti
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              value={consoles.map((v) => ({ value: v, label: v }))}
              onChange={(opts) => setConsoles((opts || []).map((o) => o.value))}
            />
          </div>
        </div>
        <div className="xbox-filter-foot">
          <span className="xbox-muted">
            Filters narrow the list only — Player Share stays based on {copy.base}.
          </span>
          <button type="button" className="xbox-pill-btn" onClick={resetFilters} disabled={!filtersActive}>
            Reset filters
          </button>
        </div>
      </div>

      {error && (
        <div className="xbox-panel xbox-error" role="alert">
          <div className="xbox-panel-title">Could not load Xbox data</div>
          <div className="xbox-muted">{error}</div>
          <button type="button" className="xbox-pill-btn" onClick={retry}>Retry</button>
        </div>
      )}

      <DashboardContent label="Xbox" loading={loading && !dataset && !error}>
      {!error && view === "cards" && (
        <>
          {(showOverview || chartIds.length > 0) && <>
          <div className="xbox-section-heading">
            <MGSectionHeader index="01" accent="paper">
              {chartIds.length ? "Player History" : "Current Top 10 & Active Users"}
            </MGSectionHeader>
            {!isPublicPreview && <MGIconButton className="xbox-section-control" aria-label="Close Xbox overview" title="Close overview"
              onClick={() => { setShowOverview(false); setChartIds([]); }}><X size={12} strokeWidth={1.5} /></MGIconButton>}
          </div>
          <div className={`xbox-overview-stage${chartIds.length ? " is-history" : ""}`}>
          <div className="xbox-overview-layer" aria-hidden={chartIds.length > 0} inert={chartIds.length ? "" : undefined}>
          <div className="xbox-featured-overview">
            <div className="xbox-featured-cards">
              <div className="xbox-featured-cards-main">
                <XboxTitleCardGrid
                  rows={cardRows}
                  loading={loading}
                  columns={columns}
                />
              </div>
            </div>
            <XboxActivityMix
              dataset={dataset}
              activityMix={pieMix}
              filteredEstimate={filteredEstimate}
              mode={mode}
              gamePass={gamePass}
              onModeSelect={isPublicPreview ? () => {} : selectOverviewMode}
              onGamePassSelect={isPublicPreview ? () => {} : setGamePass}
              onResetFilters={isPublicPreview ? () => {} : resetOverviewFilters}
            />
          </div>
          </div>
          <div className="xbox-history-layer" aria-hidden={chartIds.length === 0} inert={!chartIds.length ? "" : undefined}>
            <XboxHistoryChart
              dataset={dataset}
              mode={mode}
              rows={modeRows}
              selectedIds={chartIds}
              onAdd={toggleChart}
              onRemove={toggleChart}
              onReset={() => setChartIds([])}
            />
          </div>
          </div>
          </>}

          <div className="xbox-table-section">
          <div className="xbox-section-heading">
            <MGSectionHeader index="02" accent="paper">All Xbox Activity</MGSectionHeader>
            {!showOverview && chartIds.length === 0 && <MGIconButton className="xbox-section-control" aria-label="Show Xbox overview"
              title="Show overview" onClick={() => setShowOverview(true)}><RotateCcw size={12} strokeWidth={1.5} /></MGIconButton>}
          </div>

          <XboxActivityTable
            dataset={dataset}
            mode={mode}
            rows={filteredRows}
            loading={loading}
            chartIds={chartIds}
            onToggleChart={toggleChart}
            readOnly={isPublicPreview}
          />
          </div>
        </>
      )}

      {!error && view === "market" && (
        <>
          <XboxMarketMind rows={filteredRows} loading={loading} chartIds={chartIds} onToggleChart={toggleChart} />
        </>
      )}

      </DashboardContent>
    </div>
  );
}
